import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import FormField from "../../../components/ui/FormField";
import { Button } from "../../../components/Button";
import { Mbp4RegisterApi } from "../../../api/Mbp4RegisterApi";
import { ArrowLeft, Trash2 } from "lucide-react";


const mapPartAToDTO = (entry) => ({
  dateOfContract: entry.date || null,
  partyName: entry.party || null,
  interestedDirectorName: entry.director || null,
  relationOrNatureOfInterest: entry.relation || null,
  principalTerms: entry.terms || null,
  armsLength: entry.armsLength === "Yes" || entry.armsLength === true || false,
  boardApprovalDate: entry.boardApproval || null,
  directorsPresent: entry.present ? parseInt(entry.present, 10) : null,
  directorsInFavour: entry.favour ? parseInt(entry.favour, 10) : null,
  directorsAgainst: entry.against ? parseInt(entry.against, 10) : null,
  directorsNeutral: entry.neutral ? parseInt(entry.neutral, 10) : null,
  nextMeetingDate: entry.nextMeet || null,
  section188Reference: entry.ref188 || null,
  contractAmount: entry.amount ? parseFloat(entry.amount) : null,
  shareholdersApprovalDate: entry.shareholderApproval || null,
  remarks: entry.remarks || null,
});

const mapPartBToDTO = (entry) => ({
  bodyName: entry.entity || null,
  interestedDirectorName: entry.director || null,
  natureOfInterest: entry.nature || null,
  shareholding: entry.shareholding ? parseFloat(entry.shareholding) : null,
  dateOfInterestArisedOrChanged: entry.interestDate || null,
});

const createDefaultEntry = () => ({ id: Date.now() + Math.random() });

const MbpFourForm = () => {
  const token = useSelector((state) => state.auth.access_token);
  const companies = useSelector((state) => state.account.accountDetails);

  const [accountId, setAccountId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [partAEntries, setPartAEntries] = useState(() => {
    const saved = localStorage.getItem("mbp4_partA_data");
    return saved ? JSON.parse(saved) : [createDefaultEntry()];
  });

  const [partBEntries, setPartBEntries] = useState(() => {
    const saved = localStorage.getItem("mbp4_partB_data");
    return saved ? JSON.parse(saved) : [createDefaultEntry()];
  });

  const [signatureInfo, setSignatureInfo] = useState(() => {
    const saved = localStorage.getItem("mbp4_signature_info");
    return saved
      ? JSON.parse(saved)
      : { signature: "", place: "", date: "" };
  });

  useEffect(() => {
    localStorage.setItem("mbp4_partA_data", JSON.stringify(partAEntries));
  }, [partAEntries]);

  useEffect(() => {
    localStorage.setItem("mbp4_partB_data", JSON.stringify(partBEntries));
  }, [partBEntries]);

  useEffect(() => {
    localStorage.setItem("mbp4_signature_info", JSON.stringify(signatureInfo));
  }, [signatureInfo]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "") {
      setAccountId("");
      return;
    }
    const selectedCompany = companies.find(
      (company) => String(company.id) === String(selectedValue)
    );
    if (selectedCompany) {
      setAccountId(selectedCompany.id);
    } else {
      console.error("Company not found for value:", selectedValue);
    }
  };

  const updateSignature = (field, value) => {
    setSignatureInfo((prev) => ({ ...prev, [field]: value }));
  };

  const updatePartA = (id, field, val) => {
    setPartAEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: val } : e))
    );
  };

  const addPartA = () => {
    setPartAEntries((prev) => [...prev, createDefaultEntry()]);
  };

  const deletePartA = (id) => {
    if (partAEntries.length > 1)
      setPartAEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const updatePartB = (id, field, val) => {
    setPartBEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: val } : e))
    );
  };

  const addPartB = () => {
    setPartBEntries((prev) => [...prev, createDefaultEntry()]);
  };

  const deletePartB = (id) => {
    if (partBEntries.length > 1)
      setPartBEntries((prev) => prev.filter((e) => e.id !== id));
  };

  // ── Save / Submit ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!accountId) {
      setSubmitStatus({
        type: "error",
        message: "Please select a company before saving.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        place: signatureInfo.place || null,
        date: signatureInfo.date || null,
        signedBy: signatureInfo.signature || null,
        contractEntries: partAEntries.map(mapPartAToDTO),
        directorInterestEntries: partBEntries.map(mapPartBToDTO),
      };

      await Mbp4RegisterApi.submitMbp4Form(payload, accountId, token);

      setSubmitStatus({
        type: "success",
        message: "MBP-4 Register data saved successfully!",
      });

      localStorage.removeItem("mbp4_partA_data");
      localStorage.removeItem("mbp4_partB_data");
      localStorage.removeItem("mbp4_signature_info");

      setPartAEntries([createDefaultEntry()]);
      setPartBEntries([createDefaultEntry()]);
      setSignatureInfo({ signature: "", place: "", date: "" });
      setAccountId("");
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.message ||
          "Failed to save MBP-4 register data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Form Header */}
      <div className="bg-white px-3 py-2 rounded-xl shadow border-b-4 mb-2 text-center relative">
        <button
          onClick={() => window.history.back()}
          className="absolute top-3 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-2xl text-gray-900 uppercase">Form MBP - 4</h1>
        <h2 className="text-lg font-semibold">
          Register of contracts & bodies etc. in which directors are interested
        </h2>
        <p className="text-xs italic text-gray-600 mt-1">
          [Pursuant to section 189(1) and rule 16(1)]
        </p>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name of the company *
        </label>
        <select
          value={accountId}
          onChange={handleCompanyChange}
          className="w-50 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.accountName || `Company ${company.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* Status Message */}
      {submitStatus && (
        <div
          className={`mb-3 px-4 py-3 rounded-lg text-sm font-medium border ${
            submitStatus.type === "success"
              ? "bg-green-50 text-green-800 border-green-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          {submitStatus.message}
        </div>
      )}

      {/* Part A */}
      <h3 className="text-sm font-bold uppercase text-slate-700 mb-1">
        A. Related Party Transactions / Directors Interested
      </h3>

      <div className="space-y-2">
        {partAEntries.map((entry, i) => (
          <div key={entry.id} className="bg-white border shadow-md rounded-xl overflow-hidden">
            <div className="bg-primary text-white px-3 py-2 flex justify-between items-center">
              <span className="text-sm font-semibold text-white uppercase">Entry A-{i + 1}</span>
              {partAEntries.length > 1 && (
                <button
                  className="text-red-600 bg-white p-1 rounded-md"
                  onClick={() => deletePartA(entry.id)}
                >
                  <Trash2 size={16}/>
                </button>
              )}
            </div>

            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              <FormField
                type="date"
                label="(1) Date of contract"
                value={entry.date}
                onChange={(v) => updatePartA(entry.id, "date", v)}
              />
              <FormField
                label="(2) Party name"
                value={entry.party}
                onChange={(v) => updatePartA(entry.id, "party", v)}
              />
              <FormField
                label="(3) Interested director"
                value={entry.director}
                onChange={(v) => updatePartA(entry.id, "director", v)}
              />
              <FormField
                type="description"
                label="(4) Relation / Nature of interest"
                value={entry.relation}
                onChange={(v) => updatePartA(entry.id, "relation", v)}
              />
              <FormField
                type="description"
                label="(5) Principal terms"
                value={entry.terms}
                onChange={(v) => updatePartA(entry.id, "terms", v)}
              />
              <FormField
                label="(6) Arm's length? (Yes/No)"
                value={entry.armsLength}
                onChange={(v) => updatePartA(entry.id, "armsLength", v)}
              />
              <FormField
                type="date"
                label="(7) Board approval date"
                value={entry.boardApproval}
                onChange={(v) => updatePartA(entry.id, "boardApproval", v)}
              />
              <FormField
                label="(8) Directors present"
                value={entry.present}
                onChange={(v) => updatePartA(entry.id, "present", v)}
              />
              <FormField
                label="(9) Voted in favour"
                value={entry.favour}
                onChange={(v) => updatePartA(entry.id, "favour", v)}
              />
              <FormField
                label="(10) Voted against"
                value={entry.against}
                onChange={(v) => updatePartA(entry.id, "against", v)}
              />
              <FormField
                label="(11) Neutral"
                value={entry.neutral}
                onChange={(v) => updatePartA(entry.id, "neutral", v)}
              />
              <FormField
                type="date"
                label="(12) Next meeting date"
                value={entry.nextMeet}
                onChange={(v) => updatePartA(entry.id, "nextMeet", v)}
              />
              <FormField
                label="(13) Section 188 reference (a-g)"
                value={entry.ref188}
                onChange={(v) => updatePartA(entry.id, "ref188", v)}
              />
              <FormField
                label="(14) Contract amount"
                value={entry.amount}
                onChange={(v) => updatePartA(entry.id, "amount", v)}
              />
              <FormField
                type="date"
                label="(15) Shareholders approval date"
                value={entry.shareholderApproval}
                onChange={(v) =>
                  updatePartA(entry.id, "shareholderApproval", v)
                }
              />
              <FormField
                type="description"
                label="(16) Remarks"
                value={entry.remarks}
                onChange={(v) => updatePartA(entry.id, "remarks", v)}
              />
            </div>
          </div>
        ))}
      </div>
<div className="flex flex-row justify-end mt-2">
      <Button
        label="Add New Part A Entry"
        onClick={addPartA}
        className="text-sm"
      />
</div>

      {/* Part B */}
      <h3 className="text-sm font-bold uppercase text-slate-700 mt-0 mb-1">
        B. Directors' Interests in Other Bodies Corporate
      </h3>

      <div className="space-y-2">
        {partBEntries.map((entry, i) => (
          <div key={entry.id} className="bg-white border shadow-md rounded-xl overflow-hidden">
            <div className="bg-primary text-white px-3 py-2 flex justify-between items-center">
              <span className="text-sm font-semibold text-white uppercase">Entry B-{i + 1}</span>
              {partBEntries.length > 1 && (
                <button
                  className="text-red-600 bg-white p-1 rounded-md"
                  onClick={() => deletePartB(entry.id)}
                >
                  <Trash2 size={16}/>
                </button>
              )}
            </div>

            <div className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <FormField
                label="Company / Firm / Association"
                value={entry.entity}
                onChange={(v) => updatePartB(entry.id, "entity", v)}
              />
              <FormField
                label="Interested Director"
                value={entry.director}
                onChange={(v) => updatePartB(entry.id, "director", v)}
              />
              <FormField
                type="description"
                label="Nature of interest"
                value={entry.nature}
                onChange={(v) => updatePartB(entry.id, "nature", v)}
              />
              <FormField
                label="Shareholding (%)"
                value={entry.shareholding}
                onChange={(v) => updatePartB(entry.id, "shareholding", v)}
              />
              <FormField
                type="date"
                label="Date Interest Arose / Changed"
                value={entry.interestDate}
                onChange={(v) => updatePartB(entry.id, "interestDate", v)}
              />
            </div>
          </div>
        ))}
      </div>
<div className="flex flex-row justify-end mt-2">

      <Button
        label="Add New Part B Entry"
        onClick={addPartB}
        className="text-sm"
      />
</div>

      {/* Footer — Place / Date / Signature */}
      <div className="bg-white p-3 mt-2 rounded-lg shadow grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField
          label="Place"
          value={signatureInfo.place}
          onChange={(v) => updateSignature("place", v)}
        />
        <FormField
          type="date"
          label="Date"
          value={signatureInfo.date}
          onChange={(v) => updateSignature("date", v)}
        />
        <FormField
          label="Signed By (MD/Director/Secretary)"
          value={signatureInfo.signature}
          onChange={(v) => updateSignature("signature", v)}
        />
      </div>

      {/* Save Button */}
      <div className="flex w-full justify-end items-center">
        <Button
          label={isSubmitting ? "Saving..." : "Save MBP-4 Register"}
          onClick={handleSave}
          disabled={isSubmitting}
          className="mt-2 mb-3 mr-3 bg-green-600 hover:bg-green-700 text-white"
        />
      </div>
    </div>
  );
};

export default MbpFourForm;